import { Hint, PrimaryButton, toast, useCustomToasts } from '@systemeio/ui'
import SettingsLayout from 'modules/profile/components/settings-layout'
import { settingsMenuLocoKeys } from 'modules/profile/constants/settings-menu-loco-keys'
import { PublicApiSettingsLoco } from 'modules/profile/public-api-settings/constants/public-api-settings-loco'
import { useCreateDataPublicApiKeys } from 'modules/profile/public-api-settings/hooks/use-create-data-public-api-keys'
import { useDeletePublicApiKey } from 'modules/profile/public-api-settings/hooks/use-delete-public-api-key'
import { usePublicApiKeys } from 'modules/profile/public-api-settings/hooks/use-public-api-keys'
import {
  PublicApiSettingsCreatedInterface,
  PublicApiSettingsViewInterface,
} from 'modules/profile/public-api-settings/types/public-api-settings-interface'
import { isPublicApiSettingsAllowed } from 'modules/profile/public-api-settings/utils/is-public-api-settings-allowed'
import { NextPage } from 'next'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { CopyToClipboardButton } from 'shared/components/copy-to-clipboard-button'
import CreateModal from 'shared/components/create-modal'
import { InfoBlockOrderedListItem } from 'shared/components/info-block-ordered-list-item'
import { StatusCell } from 'shared/components/table/cells/status-cell'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import TransLoco from 'shared/components/trans-loco'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { AddIcon } from '@systemeio/ui/icons'

const PublicApiSettingsSettings: NextPage = () => {
  const { t } = useLocoTranslation()

  const router = useRouter()

  const [newCreatedKeys, setNewCreatedKeys] = useState<
    Array<Pick<PublicApiSettingsCreatedInterface, 'id' | 'token'>>
  >([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isCreateFetching, setIsCreateFetching] = useState(false)

  const [infoToastId, setInfoToastId] = useState<ReturnType<typeof infoToast> | null>(null)

  const { infoToast } = useCustomToasts()

  const { user, isWsAdmin, isWorkspaceOwner } = useUser()
  const { publicApiKeys, mutate } = usePublicApiKeys()
  const { deletePublicApiKey } = useDeletePublicApiKey()

  const { createData } = useCreateDataPublicApiKeys({
    onCreate: async createdKey => {
      await mutate(
        data =>
          data && [
            ...data,
            {
              id: createdKey.id,
              name: createdKey.name,
              createdAt: createdKey.createdAt,
              expiresAt: createdKey.expiresAt,
              maskedToken: createdKey.token,
              expired: false,
            },
          ],
        false,
      )
      setNewCreatedKeys(prev => [...prev, { id: createdKey.id, token: createdKey.token }])
      infoToastId && toast.dismiss(infoToastId)
      setInfoToastId(infoToast(t('dashboard.user_settings.public_api.copy_reminder')))
    },
  })

  const onDeletePublicApiKeys = async (deletedItem: PublicApiSettingsViewInterface) => {
    await deletePublicApiKey(deletedItem.id)
    await mutate(data => data && data?.filter(({ id }) => id !== deletedItem.id), false)
    setNewCreatedKeys(prev => prev.filter(token => token.id !== deletedItem.id))
  }

  useEffect(() => {
    if (user && !isPublicApiSettingsAllowed({ user, isWsAdmin, isWorkspaceOwner })) {
      router.replace('/')
    }
  }, [isWsAdmin, isWorkspaceOwner, router, user])

  const columns: ColumnDefinitionInterface<PublicApiSettingsViewInterface>[] = [
    {
      key: 'name',
      header: (
        <Hint label={t('dashboard.user_settings.public_api.name_tooltip')}>
          {t('dashboard.user_settings.public_api.name')}
        </Hint>
      ),
      widthPercent: 20,
    },
    {
      key: 'maskedToken',
      header: (
        <Hint label={t('dashboard.user_settings.public_api.token_tooltip')}>
          {t('dashboard.user_settings.public_api.token')}
        </Hint>
      ),
      widthPercent: 50,
      onRender: ({ id, maskedToken }) => {
        const newCreatedKey = newCreatedKeys.find(key => key.id == id)
        return newCreatedKey ? (
          <div className="flex gap-2">
            <CopyToClipboardButton
              text={newCreatedKeys.find(key => key.id == id)?.token || ''}
              className="shrink-0"
              onCopy={() => infoToastId && toast.dismiss(infoToastId)}
            />
            <p className="truncate text-sm">{newCreatedKey.token}</p>
          </div>
        ) : (
          <p className="truncate text-sm">{maskedToken}</p>
        )
      },
    },
    {
      key: 'status',
      header: t('global.status'),
      widthPercent: 10,
      onRender: ({ expired }) => (
        <div className="flex justify-center gap-2">
          <StatusCell isActive={!expired} className="shrink-0" />
        </div>
      ),
    },
    {
      key: 'expiresAt',
      header: t('dashboard.user_settings.public_api.expires_at'),
      widthPercent: 20,
      onRender: ({ expiresAt }) =>
        expiresAt === null
          ? t('dashboard.user_settings.public_api.expires_at_never')
          : dateTimeFormat(expiresAt, {
              timeFormat: user?.timeFormat,
              dateFormat: user?.dateFormat,
              timeZone: user?.timezone,
            }),
    },
  ]

  return (
    <SettingsLayout
      needSave={false}
      titles={[{ caption: t(settingsMenuLocoKeys.public_api), isStatic: true }]}
    >
      <div className="bg-background-secondary flex w-full min-w-0 flex-col rounded-md p-3 lg:p-6">
        <div
          className={`mb-6 flex h-fit flex-col gap-6
          xl:col-start-2 xl:row-start-1 xl:row-end-7`}
        >
          <Hint
            label={
              <TransLoco
                t={t}
                i18nKey="dashboard.user_settings.public_api.title_tooltip"
                components={{
                  a: (
                    <a
                      className="primary-link"
                      target="_blank"
                      rel="noreferrer"
                      href={`http://api.${process.env.NEXT_PUBLIC_SIO_HOST_NAME}/documentation`}
                    />
                  ),
                }}
              />
            }
          >
            <h3>{t('dashboard.user_settings.public_api.title_plural')}</h3>
          </Hint>
          <h4 className="text-text-primary text-[16px] font-bold leading-6">
            {t('dashboard.user_settings.public_api.instruction_title')}
          </h4>
          <ul className="flex flex-col gap-6">
            <InfoBlockOrderedListItem>
              {t('dashboard.user_settings.public_api.instruction_first')}
            </InfoBlockOrderedListItem>
            <InfoBlockOrderedListItem>
              {t('dashboard.user_settings.public_api.instruction_second')}
            </InfoBlockOrderedListItem>
          </ul>
          <p>
            <TransLoco
              t={t}
              i18nKey="dashboard.user_settings.public_api.instruction_warning"
              components={{
                b: <b />,
              }}
            />
          </p>
        </div>

        <div
          className={
            'bg-background-secondary border-text-secondary/30 mt-5 h-fit w-full min-w-0 rounded-lg border'
          }
        >
          <Table
            caption={t('dashboard.user_settings.public_api.title')}
            actions={{
              remove: {
                onChange: onDeletePublicApiKeys,
              },
            }}
            data={publicApiKeys && { hasMore: false, items: publicApiKeys }}
            columns={columns}
            emptyListWithTitles
          />

          <div className="my-4 flex justify-center">
            <PrimaryButton
              disabled={createData.disabled ? createData.disabled : !publicApiKeys}
              onClick={() => {
                if (createData.onOpen) {
                  setIsCreateFetching(true)
                  createData.onOpen().then(() => {
                    setIsCreateFetching(false)
                    setIsModalOpen(true)
                  })
                } else setIsModalOpen(true)
              }}
              isFetching={isCreateFetching}
              className="flex"
              testAttributePostfix={createData.testAttributePostfix}
            >
              <AddIcon />
              <span> {t('global.create')}</span>
            </PrimaryButton>
            <CreateModal
              opened={isModalOpen}
              onClose={() => {
                setIsModalOpen(false)
              }}
              afterLeave={createData.onAfterLeave}
              {...createData}
              onCreate={data => {
                return createData.onCreate(data)
              }}
            />
          </div>
        </div>
      </div>
    </SettingsLayout>
  )
}

export const getServerSideProps = getBaseServerSideProps(PublicApiSettingsLoco, 'global.settings')

export default PublicApiSettingsSettings
